import { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { Heart, ShieldCheck, Cpu, Globe } from "lucide-react";

const HQ_IMG = "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjBiaWxkaW5nfGVufDB8fHx8MTc3OTU0NDYxNw&ixlib=rb-4.1.0&q=85";
const ABOUT_BG = "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=80"; // collaborative team

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Navbar />
      <section className="relative max-w-6xl mx-auto px-6 py-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={ABOUT_BG} alt="Team collaborating" className="w-full h-full object-cover opacity-20 brightness-90" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(rgba(10,14,39,0.85), rgba(10,14,39,0.85))" }} />
        </div>
        <div className="label-eyebrow">About Duocapital</div>
        <h1 className="font-display text-5xl lg:text-6xl font-black mt-3 leading-tight max-w-3xl">Built for those who <span className="gold-grad">invest in pairs</span>.</h1>
        <p className="text-white/70 mt-6 max-w-2xl text-lg leading-relaxed">Duocapital was founded in 2022 with a singular mission: democratize AI-driven investing for couples, partners, and individuals alike. We believe wealth is best built side by side.</p>
        <div className="mt-8 rounded-3xl border border-white/10 bg-[#06090f]/80 p-6 max-w-3xl">
          <div className="label-eyebrow">Headquarters</div>
          <h2 className="font-display text-2xl font-bold mt-2">Duocapital headquarters</h2>
          <p className="text-white/60 mt-3">233 South Walker Drive, Suite 5712<br/>Chicago, IL 60606, United States</p>
          <p className="text-sm text-white/50 mt-4">This location represents the Duocapital headquarters concept and serves as the official company address listed on our site.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mt-16 items-center">
          <img src={HQ_IMG} alt="Conceptual Duocapital headquarters building" className="rounded-2xl border border-white/10 w-full" />
          <div>
            <h2 className="font-display text-3xl font-bold">Our Mission</h2>
            <p className="text-white/70 mt-4 leading-relaxed">Wall Street built sophisticated quant strategies for the 1%. We built them for everyone — and uniquely, for couples who want to grow their wealth together. Our proprietary AI engine processes billions of data points each day, finding edges across crypto, equities, and FX markets so you don't have to lift a finger.</p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {[
                [ShieldCheck, "Regulated & Insured"],
                [Cpu, "AI-first Architecture"],
                [Heart, "Built for Couples"],
                [Globe, "180+ Countries Served"],
              ].map(([I, t], i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="glass rounded-xl p-4 flex items-center gap-3">
                  <I className="w-5 h-5 text-[#D4AF37]" /> <span className="text-sm">{t}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 glass-strong rounded-2xl p-8">
          <div className="label-eyebrow">Regulatory Disclaimer</div>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">Duocapital is an investment management platform. All investments carry risk, including the possible loss of capital. Past performance is not indicative of future results. Returns shown are illustrative and reflect average ROI delivered by our AI trading desk during recent cycles. Users are advised to invest only what they can afford to lose. Duocapital does not provide tax or legal advice.</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
